import ApplicationController from './application_controller';

// CSV/text drop zone for the Prompt AI form (/admin/ai-usage).
//
// Reads dropped or chosen files entirely in the browser, harvests each file's
// text into a {name, content} array, and stores it as JSON in a hidden field.
// The controller folds that array into work_data.files on submit.
//
// Privacy property, stated precisely: the FILE is never uploaded — it is read in the
// browser and the File object dropped once harvested. The harvested TEXT, however, is
// folded into work_data.files, persisted on the GaiCall's request_body like the rest of
// the request, rendered on the call show page, and re-hydrated by new?from=. The form's
// helper copy says the same; keep the two honest together.
//
// Supported today: .csv / .tsv / .txt (read directly as text).
// Excel (.xlsx/.xls) is binary (zipped XML) and is refused for now — see
// extractEntries() for exactly where in-browser SheetJS extraction plugs in.
export default class extends ApplicationController {
  static targets = ['zone', 'input', 'list', 'hidden', 'notice'];

  static TEXT_FILE_RE = /\.(csv|tsv|txt)$/i;
  static IDLE_BORDER = '2px dashed #ced4da';
  static ACTIVE_BORDER = '2px dashed #0d6efd';

  connect() {
    this.files = this.readHidden();
    this.skipped = [];
    this.render();
  }

  readHidden() {
    const raw = this.hiddenTarget.value;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  openPicker() {
    this.inputTarget.click();
  }

  filesChosen(event) {
    this.ingest(event.target.files);
    // Reset so choosing the same filename again still fires a change event.
    this.inputTarget.value = '';
  }

  dragover(event) {
    event.preventDefault();
    this.zoneTarget.style.border = this.constructor.ACTIVE_BORDER;
  }

  dragleave() {
    this.zoneTarget.style.border = this.constructor.IDLE_BORDER;
  }

  drop(event) {
    event.preventDefault();
    this.zoneTarget.style.border = this.constructor.IDLE_BORDER;
    if (event.dataTransfer && event.dataTransfer.files.length) {
      this.ingest(event.dataTransfer.files);
    }
  }

  async ingest(fileList) {
    this.skipped = [];
    const groups = await Promise.all(Array.from(fileList).map((file) => this.extractEntries(file)));
    groups.flat().forEach((entry) => {
      const i = this.files.findIndex((f) => f.name === entry.name);
      if (i >= 0) this.files[i] = entry;
      else this.files.push(entry);
    });
    this.persist();
  }

  // Turn one File into zero or more {name, content} entries.
  async extractEntries(file) {
    if (this.constructor.TEXT_FILE_RE.test(file.name)) {
      const content = await file.text();
      return [{ name: file.name, content }];
    }

    // TODO(excel): .xlsx / .xls are binary (zipped XML), not text. When we add
    // support, parse here in-browser with SheetJS (the `xlsx` package) and emit
    // one entry per sheet as CSV — e.g. { name: `${file.name}#${sheetName}`,
    // content: XLSX.utils.sheet_to_csv(sheet) }. That keeps the no-upload,
    // text-only-to-gai property intact. Until then, skip and warn.
    this.skipped.push(file.name);
    return [];
  }

  remove(event) {
    const { name } = event.currentTarget.dataset;
    this.files = this.files.filter((f) => f.name !== name);
    this.persist();
  }

  persist() {
    this.hiddenTarget.value = this.files.length ? JSON.stringify(this.files) : '';
    this.render();
  }

  render() {
    this.listTarget.innerHTML = '';
    this.files.forEach((f) => {
      const li = document.createElement('li');
      li.className = 'd-flex align-items-center justify-content-between border rounded px-2 py-1 mb-1';

      const kb = ((f.content || '').length / 1024).toFixed(1);
      const label = document.createElement('span');
      label.innerHTML =
        `<i class="bi bi-file-earmark-text mr-1"></i>${this.escape(f.name)} ` +
        `<span class="text-muted">(${kb} KB)</span>`;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm btn-link text-danger p-0';
      btn.innerHTML = '<i class="bi bi-x-lg"></i>';
      btn.dataset.name = f.name;
      btn.dataset.action = 'gai-dropzone#remove';

      li.appendChild(label);
      li.appendChild(btn);
      this.listTarget.appendChild(li);
    });

    this.renderNotice();
  }

  renderNotice() {
    if (!this.hasNoticeTarget) return;
    if (this.skipped.length) {
      this.noticeTarget.innerHTML =
        '<i class="bi bi-exclamation-triangle mr-1"></i>Excel isn\'t supported yet — ' +
        `export to CSV first. Skipped: ${this.skipped.map((n) => this.escape(n)).join(', ')}`;
      this.noticeTarget.classList.remove('d-none');
    } else {
      this.noticeTarget.textContent = '';
      this.noticeTarget.classList.add('d-none');
    }
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
